import { Platform } from 'react-native'

export type Coords = { latitude: number; longitude: number }

/**
 * Get the device's current position. Works on web (navigator.geolocation) and native (expo-location).
 * Returns null if permission denied, unavailable, or error.
 */
export async function getCurrentPosition(): Promise<Coords | null> {
  if (Platform.OS === 'web') {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return null
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      )
    })
  }

  try {
    const { getCurrentPositionAsync, requestForegroundPermissionsAsync } = await import(
      'expo-location'
    )
    const { status } = await requestForegroundPermissionsAsync()
    if (status !== 'granted') return null
    const location = await getCurrentPositionAsync({ accuracy: 6 })
    if (!location?.coords) return null
    return {
      latitude: location.coords.latitude,
      longitude: location.coords.longitude,
    }
  } catch {
    return null
  }
}

export type LocationPermissionStatus = 'granted' | 'denied' | 'undetermined' | 'unknown'

/**
 * Whether this device lets the app read the position, without asking for it.
 * 'unknown' when the device cannot tell (e.g. a browser without the
 * Permissions API).
 */
export async function getLocationPermissionStatus(): Promise<LocationPermissionStatus> {
  try {
    if (Platform.OS === 'web') {
      if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown'
      const { state } = await navigator.permissions.query({ name: 'geolocation' })
      if (state === 'granted' || state === 'denied') return state
      return state === 'prompt' ? 'undetermined' : 'unknown'
    }

    const { getForegroundPermissionsAsync } = await import('expo-location')
    const { status } = await getForegroundPermissionsAsync()
    if (status === 'granted' || status === 'denied' || status === 'undetermined') return status
    return 'unknown'
  } catch {
    return 'unknown'
  }
}
